import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, Wallet, Calendar, ArrowUpRight, ArrowDownRight,
  Filter, Download
} from 'lucide-react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/format';
import FinancialHeader from './FinancialHeader';
import NewTransactionModal from './NewTransactionModal';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

export default function CashFlow() {
  const [ownerCompanyId, setOwnerCompanyId] = useState('all');
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [cashflowData, setCashflowData] = useState(null);
  const [newTxModalOpen, setNewTxModalOpen] = useState(false);

  // Load companies
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/owner-companies');
        setOwnerCompanies(res.data?.data || res.data || []);
      } catch (err) {
        console.error('Erro ao buscar empresas', err);
      }
    })();
  }, []);

  const loadCashflow = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (ownerCompanyId && ownerCompanyId !== 'all') params.owner_company_id = ownerCompanyId;

      const res = await api.get('/financial/cashflow', { params });
      setCashflowData(res.data?.data || res.data || null);
    } catch (err) {
      console.error('Erro ao carregar fluxo de caixa', err);
      toast.error('Erro ao projetar fluxo de caixa');
    } finally {
      setLoading(false);
    }
  }, [ownerCompanyId, days]);

  useEffect(() => {
    loadCashflow();
  }, [loadCashflow]);

  const timeline = cashflowData?.timeline || [];

  const handleExportCSV = () => {
    if (!timeline.length) return;
    const headers = ['Data', 'Entradas Previstas', 'Saídas Previstas', 'Saldo do Dia', 'Saldo Acumulado'];
    const rows = timeline.map(t => [
      t.date,
      t.entradas.toFixed(2),
      t.saidas.toFixed(2),
      t.saldo_dia.toFixed(2),
      t.saldo_acumulado.toFixed(2)
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `fluxo_de_caixa_${days}_dias.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Fluxo de caixa exportado');
  };

  return (
    <div className="space-y-6">
      <FinancialHeader
        title="Fluxo de Caixa"
        subtitle="Projeção diária detalhada e saldo acumulado futuro"
        ownerCompanyId={ownerCompanyId}
        setOwnerCompanyId={setOwnerCompanyId}
        ownerCompanies={ownerCompanies}
        onNewTransaction={() => setNewTxModalOpen(true)}
        onRefresh={loadCashflow}
      />

      {/* Period Selector Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-3">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-4 w-4 text-neutral-400 mr-1" />
          <span className="text-xs font-semibold text-neutral-600 dark:text-neutral-300">Horizonte de Projeção:</span>
          {[
            { label: '7 Dias', value: 7 },
            { label: '15 Dias', value: 15 },
            { label: '30 Dias', value: 30 },
            { label: '60 Dias', value: 60 },
            { label: '90 Dias', value: 90 },
            { label: '12 Meses', value: 365 },
          ].map(opt => (
            <Button
              key={opt.value}
              variant={days === opt.value ? 'default' : 'ghost'}
              size="xs"
              onClick={() => setDays(opt.value)}
              className={`h-7 text-xs ${days === opt.value ? 'bg-brand hover:bg-[#A81E23] text-white' : 'text-neutral-600 dark:text-neutral-400'}`}
            >
              {opt.label}
            </Button>
          ))}
        </div>

        <Button
          variant="outline"
          size="xs"
          onClick={handleExportCSV}
          className="h-7 text-xs gap-1.5"
        >
          <Download className="h-3 w-3" />
          Exportar CSV
        </Button>
      </div>

      {/* KPIs Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Saldo Inicial em Bancos</p>
          <p className="text-2xl font-bold mt-1 text-neutral-900 dark:text-neutral-100">
            {formatCurrency(cashflowData?.initial_balance || 0)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">Disponibilidade atual</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-emerald-600 dark:text-emerald-400">Total Entradas Previstas</p>
          <p className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">
            {formatCurrency(cashflowData?.total_entries || 0)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">Próximos {days} dias</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-red-600 dark:text-red-400">Total Saídas Previstas</p>
          <p className="text-2xl font-bold mt-1 text-red-600 dark:text-red-400">
            {formatCurrency(cashflowData?.total_exits || 0)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">Próximos {days} dias</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Saldo Final Projetado</p>
          <p className={`text-2xl font-bold mt-1 ${(cashflowData?.projected_final_balance || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            {formatCurrency(cashflowData?.projected_final_balance || 0)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">Previsão ao final de {days} dias</p>
        </div>
      </div>

      {/* Gráfico Composto: Entradas, Saídas e Linha de Saldo Acumulado */}
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
        <div className="mb-4">
          <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
            Evolução e Curva de Caixa ({days} dias)
          </h3>
          <p className="text-xs text-neutral-500">
            Barras mostram as movimentações diárias e a linha azul reflete a liquidez acumulada no tempo
          </p>
        </div>

        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={timeline} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="date" fontSize={11} stroke="#888" tickFormatter={d => d.substring(5)} />
              <YAxis fontSize={11} stroke="#888" tickFormatter={v => `R$ ${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(v) => [formatCurrency(v)]}
                contentStyle={{ backgroundColor: 'rgba(23, 23, 23, 0.95)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="entradas" name="Entradas (+)" fill="#10B981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="saidas" name="Saídas (-)" fill="#EF4444" radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="saldo_acumulado" name="Saldo Acumulado" stroke="#2563EB" strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tabela de Extrato Diário */}
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden shadow-xs">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">Extrato Projetado Dia a Dia</h3>
          <p className="text-xs text-neutral-500">Posição detalhada de caixa para cada dia do horizonte</p>
        </div>

        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-xs text-left">
            <thead className="bg-neutral-50 dark:bg-neutral-800/50 text-neutral-500 font-semibold sticky top-0 border-b border-neutral-200 dark:border-neutral-800">
              <tr>
                <th className="py-2.5 px-4">Data</th>
                <th className="py-2.5 px-4 text-right">Entradas Previstas</th>
                <th className="py-2.5 px-4 text-right">Saídas Previstas</th>
                <th className="py-2.5 px-4 text-right">Resultado do Dia</th>
                <th className="py-2.5 px-4 text-right">Saldo Acumulado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={5} className="py-3 px-4">
                      <div className="h-4 bg-neutral-100 dark:bg-neutral-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : timeline.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-neutral-400">
                    Nenhuma projeção disponível para este período.
                  </td>
                </tr>
              ) : (
                timeline.map((row) => (
                  <tr key={row.date} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/40">
                    <td className="py-2.5 px-4 font-medium text-neutral-900 dark:text-neutral-100">
                      {formatDate(row.date)}
                    </td>
                    <td className="py-2.5 px-4 text-right text-emerald-600 dark:text-emerald-400 font-medium">
                      {row.entradas > 0 ? formatCurrency(row.entradas) : '-'}
                    </td>
                    <td className="py-2.5 px-4 text-right text-red-600 dark:text-red-400 font-medium">
                      {row.saidas > 0 ? formatCurrency(row.saidas) : '-'}
                    </td>
                    <td className={`py-2.5 px-4 text-right font-semibold ${row.saldo_dia > 0 ? 'text-emerald-600' : row.saldo_dia < 0 ? 'text-red-600' : 'text-neutral-400'}`}>
                      {row.saldo_dia !== 0 ? formatCurrency(row.saldo_dia) : '-'}
                    </td>
                    <td className={`py-2.5 px-4 text-right font-bold ${row.saldo_acumulado >= 0 ? 'text-neutral-900 dark:text-neutral-100' : 'text-red-600'}`}>
                      {formatCurrency(row.saldo_acumulado)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <NewTransactionModal
        open={newTxModalOpen}
        onOpenChange={setNewTxModalOpen}
        onSuccess={loadCashflow}
      />
    </div>
  );
}
